import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'framer-motion';

const COLORS = ['#FFADAD', '#FFD6A5', '#FDFFB6', '#CAFFBF', '#9BF6FF', '#A0C4FF', '#BDB2FF', '#FFC6FF'];
const GRID_SIZE = 8;

const InfinitePop = () => {
  const navigate = useNavigate();
  const [bubbles, setBubbles] = useState([]);
  const [poppedCount, setPoppedCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioContextRef = useRef(null);
  const bubblesRef = useRef([]);
  const timeoutsRef = useRef(new Set());

  // Initialize audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play bubble pop sound
  const playPopSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create a satisfying "pop" sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      // Pop characteristics - quick high frequency burst
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(400, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.4, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);
      
      // Add a "release" sound - like air escaping
      const noise = ctx.createOscillator();
      const noiseGain = ctx.createGain();
      
      noise.type = 'triangle';
      noise.frequency.setValueAtTime(800, ctx.currentTime);
      noise.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.08);
      
      noiseGain.gain.setValueAtTime(0.15, ctx.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      
      noise.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      
      noise.start(ctx.currentTime);
      noise.stop(ctx.currentTime + 0.08);
      
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [soundEnabled, initAudio]);

  useEffect(() => {
    initBubbles();

    return () => {
      timeoutsRef.current.forEach(id => clearTimeout(id));
      timeoutsRef.current.clear();
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Keep a live ref of the bubbles state so click handlers never act on a
  // stale render-time snapshot.
  useEffect(() => {
    bubblesRef.current = bubbles;
  }, [bubbles]);

  const initBubbles = () => {
    const newBubbles = Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, i) => ({
      id: i,
      popped: false,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]
    }));
    bubblesRef.current = newBubbles;
    setBubbles(newBubbles);
  };

  const handlePop = (id) => {
    playPopSound();
    setBubbles(prev => prev.map(b => b.id === id ? { ...b, popped: true } : b));
    setPoppedCount(prev => prev + 1);

    // Regenerate after delay
    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      setBubbles(prev => prev.map(b =>
        b.id === id
          ? { ...b, popped: false, color: COLORS[Math.floor(Math.random() * COLORS.length)] }
          : b
      ));
    }, 2500);
    timeoutsRef.current.add(timeoutId);
  };

  // Guards against popping an already-popped bubble using a live ref (not a
  // stale render-time closure), and is wired to onClick so it also fires
  // from native keyboard (Enter/Space) and switch-access activation.
  const attemptPop = (id) => {
    const target = bubblesRef.current.find(b => b.id === id);
    if (!target || target.popped) return;
    // Mark as popped in the ref synchronously so a rapid second activation
    // (before React re-renders) is also blocked, not just the state update.
    bubblesRef.current = bubblesRef.current.map(b => b.id === id ? { ...b, popped: true } : b);
    handlePop(id);
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (!soundEnabled) {
      // Initialize audio context when enabling sound (requires user interaction)
      initAudio();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0F5] to-[#E0F7FA] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-300 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <header className="flex flex-wrap justify-between items-center gap-4 mb-6 md:mb-8">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-[#2D3748] mb-1">Infinite Pop</h1>
            <p className="text-base md:text-lg text-[#4A5568]">
              Popped: <span className="font-bold text-[#81B29A]">{poppedCount}</span>
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              data-testid="toggle-sound"
              onClick={toggleSound}
              className={`px-4 py-2 md:py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 shadow-sm ${
                soundEnabled 
                  ? 'bg-[#81B29A] text-white' 
                  : 'bg-white text-[#4A5568]'
              }`}
              title={soundEnabled ? 'Mute sound' : 'Enable sound'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            
            <button
              data-testid="reset-bubbles"
              onClick={() => {
                timeoutsRef.current.forEach(id => clearTimeout(id));
                timeoutsRef.current.clear();
                initBubbles();
                setPoppedCount(0);
              }}
              className="bg-[#81B29A] text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 shadow-lg flex items-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          </div>
        </header>

        <div className="bg-white/60 backdrop-blur-md rounded-3xl p-4 md:p-8 shadow-xl border border-white/40">
          <div 
            className="grid gap-2 md:gap-3" 
            style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))` }}
          >
            {bubbles.map(bubble => (
              <motion.button
                key={bubble.id}
                data-testid={`bubble-${bubble.id}`}
                onClick={() => attemptPop(bubble.id)}
                className="aspect-square rounded-full shadow-md hover:shadow-lg transition-shadow relative overflow-hidden"
                style={{ 
                  backgroundColor: bubble.popped ? '#E5E5E5' : bubble.color,
                  touchAction: 'manipulation'
                }}
                animate={{
                  scale: bubble.popped ? 0.6 : 1,
                  opacity: bubble.popped ? 0.3 : 1
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                whileHover={{ scale: bubble.popped ? 0.6 : 1.15 }}
                whileTap={{ scale: 0.7 }}
              >
                {/* Bubble shine effect */}
                {!bubble.popped && (
                  <>
                    <div className="absolute top-1 left-1 md:top-2 md:left-2 w-2 h-2 md:w-4 md:h-4 bg-white/50 rounded-full"></div>
                    <div className="absolute top-2 left-3 md:top-3 md:left-4 w-1 h-1 md:w-2 md:h-2 bg-white/30 rounded-full"></div>
                  </>
                )}
              </motion.button>
            ))}
          </div>
        </div>

        <div className="mt-6 md:mt-8 text-center">
          <p className="text-[#4A5568] text-base md:text-lg">
            {soundEnabled ? '🔊 ' : '🔇 '}
            Pop bubbles for satisfying feedback! They regenerate after a few seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InfinitePop;
